import { FieldRow, Form } from '@/components';
import { useTrackPersonalizationSummaryActivityMutation } from '@/redux';
import { Col, Modal } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

const ActivityStorageKey = 'PersonalizationSummary:Activity';

type ActivityInfo = {
  name: string;
  company: string;
};

const INITIAL_VALUE: ActivityInfo = {
  name: '',
  company: '',
};

const SCHEMA: Yup.AnySchema = Yup.object().shape({
  name: Yup.string().field().nullable().required(),
  company: Yup.string().field().nullable().required(),
});

export function useTrackPersonalizationActivityModal(homeId?: string) {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const onSubmit = useSubmit(homeId, () => setModalOpen(false));

  // 🔒 prevents repeated execution for the same homeId
  const handledHomeIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!homeId) {
      setModalOpen(false);
      handledHomeIdRef.current = undefined;
      return;
    }

    // ✅ already handled for this homeId
    if (handledHomeIdRef.current === homeId) {
      return;
    }

    handledHomeIdRef.current = homeId;

    const stored = window.sessionStorage.getItem(ActivityStorageKey);

    if (!stored) {
      setModalOpen(true);
      return;
    }

    const activity: ActivityInfo = JSON.parse(stored);

    onSubmit(activity);
    setModalOpen(false);
  }, [homeId, onSubmit]);

  return useMemo(() => ({ modalOpen, onSubmit }), [modalOpen, onSubmit]);
}

const useSubmit = (homeId: string | undefined, closeModal: () => any) => {
  const [trackActivity] = useTrackPersonalizationSummaryActivityMutation();

  return useCallback(
    async (values: ActivityInfo) => {
      if (homeId)
        await trackActivity({
          homeId: homeId,
          name: values.name,
          company: values.company,
        })
          .unwrap()
          .then(() => {
            window.sessionStorage.setItem(
              ActivityStorageKey,
              JSON.stringify({
                homeId: homeId,
                name: values.name,
                company: values.company,
              }),
            );
          })
          .then(closeModal);
    },

    [closeModal, homeId, trackActivity],
  );
};

export function TrackPersonalizationActivityModal(props: {
  modalOpen: boolean;
  onSubmit: (value: ActivityInfo) => void;
}) {
  const { modalOpen, onSubmit } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.activity',
  });
  return (
    <Modal
      title={t('modal.title')}
      visible={modalOpen}
      closable={false}
      footer={[
        <Form.Submit
          uid="personalization-summary-activity"
          key="submit"
          type="primary"
        >
          {t('modal.ok')}
        </Form.Submit>,
      ]}
    >
      <Form.Formik<ActivityInfo>
        uid="personalization-summary-activity"
        i18n="personalization.activity.details"
        initialValues={INITIAL_VALUE}
        validationSchema={SCHEMA}
        onSubmit={onSubmit}
      >
        <>
          <FieldRow>
            <Col span={12}>
              <Form.Input name="name" required />
            </Col>
            <Col span={12}>
              <Form.Input name="company" required />
            </Col>
          </FieldRow>
        </>
      </Form.Formik>
    </Modal>
  );
}
